import { Icon, StepProgressBar } from '@amelio/ui-library';
import { useId } from 'react';

import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';

/** The four stops of the evolution track, in order; the bar spaces them evenly. */
const MILESTONE_LABEL_KEYS = [
    'performanceProfile.growth.evolution.start',
    'performanceProfile.growth.evolution.consolidation',
    'performanceProfile.growth.evolution.expertise',
    'performanceProfile.growth.evolution.ready',
] as const;

interface EvolutionBarProps {
    /** Readiness out of 100; `null` while the proficiency scale is unknown, or when the target
     *  profile lists no competency. The track is then not drawn at all: a bar is the drawing of a
     *  figure, and drawn at zero it would place the employee at the start of a journey nobody has
     *  measured yet -- to the eye, and to a screen reader, which hears "Start, 0%". The card's own
     *  message says why the figure is missing. */
    score: number | null;
    /** Names the employee in the note about the target position; `null` hides the note, when no
     *  target is set. The note never says who chose the target: the manager always sets it. */
    employeeFirstName: string | null;
    /** Shown under the header, after the track: what the journey waits on when it cannot be drawn. */
    children?: ReactNode;
    dataTestId?: string;
}

/**
 * The "Parcours d'évolution" card of the next-target section: the note about the target position,
 * the header, and the library's step bar. The readiness is only drawn by the bar, never written as
 * a number: a score out of 100 next to the title would compete with the performance score of the
 * neighbouring card. The warm surface belongs to this card, not to the bar, which is also used on
 * neutral backgrounds.
 *
 * The sizes the reference drawing fixes are written out where the drawing lands between two steps
 * of the scale; elsewhere they read the step, which `app.css` declares in real pixels.
 */
export const EvolutionBar = ({
    score,
    employeeFirstName,
    children,
    dataTestId = 'growth-evolution-bar',
}: EvolutionBarProps) => {
    const { t } = useAmelioTranslations();
    const titleId = useId();

    const journeyTitle = t('performanceProfile.growth.evolution.journey');

    return (
        <section
            className={
                'from-growth-ember-wash to-growth-violet-wash w-full rounded-[24px] border border-white bg-gradient-to-br via-white p-[20px] shadow-sm'
            }
            aria-labelledby={titleId}
            data-testid={dataTestId}
        >
            {employeeFirstName !== null && (
                <p className={'text-navy/70 mb-[16px] text-[13px] leading-[20px]'}>
                    {t('performanceProfile.growth.evolution.targetNote', {
                        firstName: employeeFirstName,
                    })}
                </p>
            )}

            <header className={'mb-[16px] flex min-w-0 items-center gap-[8px]'}>
                <Icon
                    name={'essential-flash'}
                    size={24}
                    colorClassName={'text-growth-ember'}
                    className={'shrink-0'}
                    ariaHidden
                />
                <div className={'min-w-0'}>
                    <h4
                        id={titleId}
                        className={'text-navy text-[14px] leading-tight font-semibold'}
                    >
                        {journeyTitle}
                    </h4>
                    <p className={'text-navy/60 text-[11px] leading-tight'}>
                        {t('performanceProfile.growth.evolution.subtitle')}
                    </p>
                </div>
            </header>

            {score !== null && (
                <StepProgressBar
                    steps={MILESTONE_LABEL_KEYS.map((labelKey) => ({
                        id: labelKey,
                        label: t(labelKey),
                    }))}
                    value={score}
                    gradientVariant={'warm'}
                    milestoneLayout={'aligned'}
                    emphasizeLastStep
                    badge={
                        <Icon
                            name={'school-teacher'}
                            size={'md'}
                            color={'inherit'}
                            ariaHidden
                        />
                    }
                    ariaLabel={journeyTitle}
                    dataTestId={`${dataTestId}-track`}
                />
            )}
            {children}
        </section>
    );
};
